import { calcPriceIncludingTax } from '@/utils/priceIncludingTax';

import type { WabunkaExperienceEventExperienceEventOption } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_experience_event_option';
import type { WabunkaExperienceEventOption, WabunkaExperienceEventOptionDetailedImage } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_option';

/** 体験の詳細画像 */
export interface WabunkaExperienceEventOptionDetailedImageViewModel {
  /** ID */
  id: string;
  /** 体験イベントオプションID */
  experienceEventOptionId: string;
  /** ファイルパス */
  imageFilepath: string;
  /** シーケンスID */
  sequenceId: number;
}

/** 体験イベントオプションマスタ */
export interface WabunkaExperienceEventOptionViewModel {
  /** ID */
  id: string;
  /** オプション名 */
  name: string;
  /** 管理用名称 */
  nameForAdmin: string;
  /** 説明 */
  description: string;
  /** 詳細説明 */
  detailedDescription: string;
  /** 注釈 */
  annotation: string;
  /** サムネイル画像のファイルパス */
  mainImageFilepath: string;
  /** 金額(税抜) */
  price: number;
  /** 税率 */
  taxRate: number;
  /** シーケンスID */
  sequenceId: number;
  /** 体験オプション選択肢 */
  detailedImages: WabunkaExperienceEventOptionDetailedImageViewModel[];
  /**
   * 追加プロパティ
   */
  /** 金額(税込) */
  priceIncludingTax: number;
}
/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaExperienceEventOptionDetailedImage} res - APIレスポンス
 * @returns {WabunkaExperienceEventOptionDetailedImageViewModel} - ビューモデル
 */
const buildWabunkaExperienceEventOptionDetailedImageListViewModel = (res: WabunkaExperienceEventOptionDetailedImage[]): WabunkaExperienceEventOptionDetailedImageViewModel[] => {
  return res
    .map((experienceEventOptionDetailedImage: WabunkaExperienceEventOptionDetailedImage) => ({
      id: experienceEventOptionDetailedImage.id,
      experienceEventOptionId: experienceEventOptionDetailedImage.experienceEventOptionId,
      imageFilepath: experienceEventOptionDetailedImage.imageFilepath,
      sequenceId: experienceEventOptionDetailedImage.sequenceId,
    }))
    .sort((a, b) => a.sequenceId - b.sequenceId);
};

/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaExperienceEventExperienceEventOption} res - APIレスポンス
 * @returns {WabunkaExperienceEventOptionViewModel} - ビューモデル
 */
export const buildWabunkaExperienceEventOptionListViewModel = (res: WabunkaExperienceEventExperienceEventOption[]): WabunkaExperienceEventOptionViewModel[] =>
  res
    .filter((experienceEventExperienceEventOption: WabunkaExperienceEventExperienceEventOption) => {
      return experienceEventExperienceEventOption.experienceEventOption !== undefined;
    })
    .map((experienceEventExperienceEventOption: WabunkaExperienceEventExperienceEventOption) => {
      const experienceEventOption = experienceEventExperienceEventOption.experienceEventOption as WabunkaExperienceEventOption;
      return {
        id: experienceEventOption.id,
        name: experienceEventOption.name,
        nameForAdmin: experienceEventOption.nameForAdmin,
        description: experienceEventOption.description,
        detailedDescription: experienceEventOption.detailedDescription,
        annotation: experienceEventOption.annotation,
        mainImageFilepath: experienceEventOption.mainImageFilepath,
        price: experienceEventOption.price,
        taxRate: experienceEventOption.taxRate,
        priceIncludingTax: calcPriceIncludingTax(experienceEventOption.price, experienceEventOption.taxRate),
        sequenceId: experienceEventExperienceEventOption.sequenceId,
        detailedImages: buildWabunkaExperienceEventOptionDetailedImageListViewModel(experienceEventOption.detailedImages),
      };
    })
    .sort((a, b) => a.sequenceId - b.sequenceId);
